import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import {
  fetchChampionSeasonStats,
  fetchSeasonStatsMeta,
  type ChampionSeasonStatsParams,
} from "../../../services/services";
import { getErrorMessage } from "../../../services/utils/apiError";
import { IChampionSeasonStats } from "../../../types/types";
import {
  CHAMPION_ROLES,
  defaultChampionStatsSortDirection,
  hasChampionDraftData,
  hasChampionRoleData,
  isDraftSortKey,
  type ChampionStatsSortKey,
} from "../../../utils/championstats/table";
import { formatSeasonName } from "../../../utils/seasonOrder";
import type { SortDirection } from "../../../utils/seasonstats/table";

// Long enough to skip intermediate keystrokes, short enough to feel immediate.
const SEARCH_DEBOUNCE_MS = 250;

type ChampionStatsSortState = {
  key: ChampionStatsSortKey;
  direction: SortDirection;
};

type ChampionStatsSortAction =
  | { type: "column"; key: ChampionStatsSortKey }
  | { type: "prefer"; key: ChampionStatsSortKey };

const pickSelectableSeason = (current: string, seasons: string[], live: string): string => {
  if (current && seasons.includes(current)) return current;
  if (live && seasons.includes(live)) return live;
  return seasons[0] || "";
};

const championStatsSortReducer = (
  state: ChampionStatsSortState,
  action: ChampionStatsSortAction,
): ChampionStatsSortState => {
  if (action.type === "prefer") {
    if (state.key === action.key) return state;
    return { key: action.key, direction: defaultChampionStatsSortDirection(action.key) };
  }

  if (state.key === action.key) {
    return { ...state, direction: state.direction === "asc" ? "desc" : "asc" };
  }

  return { key: action.key, direction: defaultChampionStatsSortDirection(action.key) };
};

export const useChampionStats = () => {
  const { strings } = useLocalization();
  const copy = strings.CHAMPION_STATS;

  const [seasons, setSeasons] = useState<string[]>([]);
  const [currentSeason, setCurrentSeason] = useState("");
  const [selectedSeason, setSelectedSeasonState] = useState("");
  const [rows, setRows] = useState<IChampionSeasonStats[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [roleFilterAvailable, setRoleFilterAvailable] = useState(true);
  const [sort, dispatchSort] = useReducer(championStatsSortReducer, {
    key: "presence" as ChampionStatsSortKey,
    direction: "desc" as SortDirection,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const requestIdRef = useRef(0);

  const loadedSeasonRef = useRef("");
  const selectedSeasonRef = useRef(selectedSeason);
  const sortKeyRef = useRef(sort.key);
  selectedSeasonRef.current = selectedSeason;
  sortKeyRef.current = sort.key;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setSelectedSeason = (season: string) => {
    setRoleFilter("all");
    if (season === currentSeason && currentSeason !== "" && isDraftSortKey(sort.key)) {
      dispatchSort({ type: "prefer", key: "gamesCounted" });
    }
    setSelectedSeasonState(season);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    void (async () => {
      try {
        const seasonMeta = await fetchSeasonStatsMeta();
        if (!mountedRef.current) return;

        const selectableSeasons = seasonMeta.seasons;
        const nextSeason = pickSelectableSeason(
          selectedSeasonRef.current,
          selectableSeasons,
          seasonMeta.currentSeason,
        );
        setSeasons(selectableSeasons);
        setCurrentSeason(seasonMeta.currentSeason);
        if (!selectableSeasons.length) {
          setRows([]);
          setLoading(false);
          setRefreshing(false);
        }
        if (
          nextSeason !== "" &&
          nextSeason === seasonMeta.currentSeason &&
          isDraftSortKey(sortKeyRef.current)
        ) {
          dispatchSort({ type: "prefer", key: "gamesCounted" });
        }
        setSelectedSeasonState(nextSeason);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(getErrorMessage(err, copy.ERROR_SEASONS));
        setLoading(false);
      }
    })();
  }, [copy.ERROR_SEASONS]);

  useEffect(() => {
    if (!selectedSeason) return;

    const requestId = ++requestIdRef.current;
    const isStale = () => !mountedRef.current || requestIdRef.current !== requestId;

    const seasonChanged = loadedSeasonRef.current !== selectedSeason;
    if (seasonChanged) {
      setRows([]);
      setRefreshing(false);
      setLoading(true);
      setRoleFilterAvailable(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    const params: ChampionSeasonStatsParams = {
      season: selectedSeason,
      champion: debouncedSearch || undefined,
      role: roleFilter === "all" ? undefined : roleFilter,
      sortKey: sort.key,
      direction: sort.direction,
    };

    void (async () => {
      try {
        const champions = await fetchChampionSeasonStats(params);
        if (isStale()) return;
        loadedSeasonRef.current = selectedSeason;
        setRows(champions);
        if (roleFilter === "all" && !debouncedSearch) {
          setRoleFilterAvailable(hasChampionRoleData(champions));
        }
        if (!hasChampionDraftData(champions) && isDraftSortKey(sort.key)) {
          dispatchSort({ type: "prefer", key: "gamesCounted" });
        }
      } catch (err) {
        if (isStale()) return;
        setError(getErrorMessage(err, copy.ERROR_CHAMPIONS));
      } finally {
        if (!isStale()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
  }, [selectedSeason, debouncedSearch, roleFilter, sort.key, sort.direction, copy.ERROR_CHAMPIONS]);

  // Seasons imported from a champion sheet get the draft columns; live seasons
  // have only what player_game_stats can tell us.
  const showDraftColumns = useMemo(() => hasChampionDraftData(rows), [rows]);

  const formatSeasonLabel = (season: string) => {
    const label = formatSeasonName(season, strings.SEASON_SPLITS);
    return season === currentSeason ? `${label} (${copy.CURRENT_SEASON_LIVE})` : label;
  };

  const isLiveSeason = selectedSeason === currentSeason && currentSeason !== "";

  const emptyMessage =
    debouncedSearch || roleFilter !== "all" ? copy.NO_RESULTS : copy.NO_SEASON_STATS;

  return {
    copy,
    seasons,
    selectedSeason,
    setSelectedSeason,
    rows,
    search,
    setSearch,
    roleFilter,
    setRoleFilter,
    championRoles: CHAMPION_ROLES,
    roleLabels: strings.SEASON_STATS.ROLES,
    sortKey: sort.key,
    sortDirection: sort.direction,
    handleSort: (key: ChampionStatsSortKey) => {
      dispatchSort({ type: "column", key });
    },
    loading,
    refreshing,
    error,
    formatSeasonLabel,
    isLiveSeason,
    showDraftColumns,
    roleFilterAvailable,
    emptyMessage,
  };
};
